import { AlertCircle, PencilIcon } from 'lucide-react';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';

import { formatCardBrand, formatCardExpiry } from '@/shared/lib/format-utils';

import type { CardExpiryWarning } from '../../../model/get-card-expiry-warning';

type Props = {
    brand: string | null;
    last4: string;
    expMonth: number | null;
    expYear: number | null;
    expiryWarning: CardExpiryWarning | null;
    onEdit: () => void;
};

const EXPIRY_WARNING_LABEL: Record<CardExpiryWarning, string> = {
    expired: 'Card expired',
    'before-next-bill': 'Card expires before your next bill',
};

export function PlanBillingSummaryPaymentMethod({
    brand,
    last4,
    expMonth,
    expYear,
    expiryWarning,
    onEdit,
}: Props) {
    const expiry = formatCardExpiry(expMonth, expYear);
    const brandAndLastFour = [formatCardBrand(brand), `***${last4}`].filter(Boolean).join(' ');

    return (
        <div className="flex items-center gap-2">
            {expiryWarning && (
                // Lucide strokes the circle as well as filling it, and the stroke eats into its own fill.
                <AlertCircle
                    role="img"
                    aria-label={EXPIRY_WARNING_LABEL[expiryWarning]}
                    className="size-5 shrink-0 fill-destructive text-background [&>circle]:stroke-none"
                />
            )}
            <p className="text-base">
                {brandAndLastFour}
                {expiry && (
                    <>
                        ,{' '}
                        <span className={cn(expiryWarning && 'text-destructive')}>
                            exp {expiry}
                        </span>
                    </>
                )}
            </p>
            <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="Edit payment method"
                onClick={onEdit}
            >
                <PencilIcon className="size-4" />
            </Button>
        </div>
    );
}
